import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RichTextEditor } from "@/components/RichTextEditor";

export function AccountSignatureDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const [sig, setSig] = useState(user?.email_signature || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setSig(user?.email_signature || "");
  }, [open, user]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me/signature", { email_signature: sig });
      setUser({ ...user, email_signature: data.email_signature });
      toast.success("Signature saved — it will be added to all your outbound emails");
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not save signature");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="signature-dialog">
        <DialogHeader>
          <DialogTitle>My Email Signature</DialogTitle>
          <DialogDescription>
            Added to the bottom of every transaction email you send as coordinator (Notify Parties and per-task emails).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <RichTextEditor
            value={sig}
            onChange={setSig}
            placeholder="e.g. Jane Doe · Transaction Coordinator · MattC Team · (513) 555-0100"
            testid="signature-editor"
          />
          {sig?.trim() ? (
            <div>
              <div className="text-[10px] tracking-[0.12em] uppercase text-[#a3a099] font-semibold mb-1">Preview</div>
              <div
                data-testid="signature-preview"
                className="border-t border-[#E5E5E2] pt-3 text-[13px] leading-relaxed text-[#575755]"
                dangerouslySetInnerHTML={{ __html: sig }}
              />
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="signature-cancel-button">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="signature-save-button">
            {saving ? "Saving…" : "Save signature"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
